import { Service } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Scope, User } from '@sentry/angular';

import { describeRequestError } from './describe-request-error.util';
import { SentryConfig } from './sentry-config.model';

const BLOCKED_EMAIL_DOMAINS: string[] = ['mailinator.com', 'testinator.com'];
const VERSION_REGEX = /<base[^>]*\bhref=['"][^'"]*\/(\d+\.\d+\.\d+)\/?['"]/i;

@Service()
export class SentryService {
  #enabled = false;
  #version = 'local';

  captureException(errorId: string, error: unknown): void {
    if (!this.#enabled) {
      return;
    }

    Sentry.withScope((scope: Scope) => {
      scope.setTag('application', 'app-frontend');
      scope.setTag('errorId', errorId);
      scope.setTag('version', this.#version);

      Sentry.captureException(error);
    });
  }

  captureMessage(level: Sentry.SeverityLevel, message: string, attributes?: Record<string, unknown>): void {
    if (!this.#enabled) {
      return;
    }

    Sentry.captureMessage(message, {
      level: level as Sentry.SeverityLevel,
      extra: attributes,
    });
  }

  identify(user: User): void {
    if (!this.#enabled) {
      return;
    }

    Sentry.setUser(user);
  }

  initialize(config: SentryConfig): void {
    const { dsn, enabled, environment, recognizeFailedRequest } = config;

    if (!(enabled && dsn)) {
      return;
    }

    this.#version = document.documentElement.outerHTML.match(VERSION_REGEX)?.[1] ?? 'local';

    Sentry.init({
      dataCollection: {
        cookies: false,
        httpBodies: [],
        httpHeaders: false,
        userInfo: false,
      },
      dsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.userTimingIntegration({ ignore: [/^boomr:/] }),
        Sentry.replayIntegration({ blockAllMedia: true, maskAllText: true }),
      ],
      propagateTraceparent: true,
      release: `release-${this.#version}`,
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      tracePropagationTargets: ['localhost', /^\/api/, /\/v3\/api\//],
      tracesSampleRate: 1.0,
      beforeSend: (event, hint) => {
        const email = event.user?.email?.toLowerCase();

        if (email && BLOCKED_EMAIL_DOMAINS.some((domain) => email.endsWith(domain))) {
          return null;
        }

        return describeRequestError(event, hint, recognizeFailedRequest);
      },
    });

    this.#enabled = true;
  }

  reset(): void {
    if (!this.#enabled) {
      return;
    }

    Sentry.setUser(null);
  }
}
